import { useState } from 'react';
import { accounts } from '../backend';
import { useApp } from '../store';
import Logo from './Logo';

/**
 * CHOOSE A PASSWORD, BEFORE THE BOARD.
 *
 * An admin makes the account and the person makes the password. Nobody else
 * ever knows it — there is no temporary one to leak, to forget to change, or to
 * be read off a chat message a year later.
 *
 * WHY IT IS A GATE RATHER THAN A SUGGESTION. Somebody invited arrives holding
 * nothing but a link from their inbox, and a link in an inbox is a key anybody
 * who can read that inbox also holds. Until they have a password of their own,
 * that is the only thing standing between an old forwarded email and their
 * account. Offering it as an option means most people never do it.
 *
 * Rendered INSTEAD of the app, not over it — the same rule as the terms. A
 * dialog laid over a board is a board that was drawn, and anything drawn can be
 * read.
 */
export default function ChoosePassword() {
  const setMyPassword = useApp((s) => s.setMyPassword);
  const email = useApp((s) => s.user?.email ?? '');
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState('');

  /*
   * Eight, and said before they type rather than after. A rule announced only on
   * failure is a rule somebody meets by guessing.
   */
  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = again.length > 0 && again !== password;
  const ready = password.length >= 8 && again === password && !busy;

  const save = () => {
    if (!ready) return;
    setBusy(true);
    setTrouble('');
    void accounts.setPassword(password).then(
      async () => {
        // Recorded so they are never asked again — see Profile.passwordSetAt.
        await setMyPassword();
        setBusy(false);
      },
      (e: unknown) => {
        setBusy(false);
        setTrouble(
          e instanceof Error && /weak|short/i.test(e.message)
            ? 'That password is too easy to guess. Try a longer one.'
            : `That could not be saved — ${e instanceof Error ? e.message : String(e)}`
        );
      }
    );
  };

  return (
    <div className="login-wrap">
      <form
        className="login-card"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <div className="brand login-brand">
          <Logo tile={34} mark={21} />
        </div>
        <h2>Choose a password</h2>
        <p className="hint">
          You are signed in as <strong>{email}</strong>. Set a password now and you can sign in
          without waiting for an email each time.
        </p>

        <label className="stack">
          New password
          <input
            className="input"
            type="password"
            autoFocus
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <p className="hint">At least eight characters. Longer is better than complicated.</p>

        <label className="stack">
          Type it again
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            value={again}
            onChange={(e) => setAgain(e.target.value)}
          />
        </label>

        {tooShort && <div className="login-error">Eight characters or more, please.</div>}
        {mismatch && <div className="login-error">Those two do not match.</div>}
        {trouble && <div className="login-error">{trouble}</div>}

        <button className="btn btn-primary" type="submit" disabled={!ready}>
          {busy ? 'Saving…' : 'Save and carry on'}
        </button>
      </form>
    </div>
  );
}
